import { EMPTY_STRING } from '@rotomeca/utils';
import { Autobind, Define, Light, SetAttr } from '../../../core/decorators';
import { TAG_HIDE } from '../../../core/utils/constants/tags';
import { Nullable } from '@rotomeca/utils';
import { ValueOf } from '../../../core/utils/types';
import BnumElementInternal from '../../bnum-element-states';
import { Observe } from 'core/decorators/observe';
import { UpdateAll } from 'core/decorators/updateAll';

/**
 * Liste des points de rupture (breakpoints) gérés par le composant.
 */
export const BREAKPOINTS = {
	/** Mobile */
	phone: 480,
	/** Tablette portrait */
	small: 768,
	/** Tablette paysage / Ordinateurs portables tactiles */
	touch: 1024,
	/** Bureau */
	normal: 1200,
} as const;

/**
 * Type représentant les valeurs des points de rupture.
 */
export type BREAKPOINTS = ValueOf<typeof BREAKPOINTS>;

/**
 * Liste des modes de masquage.
 */
export const MODES = {
	/** En dessous du breakpoint */
	DOWN: 'down',
	/** Au-dessus du breakpoint */
	UP: 'up',
} as const;

/**
 * Type représentant les modes de masquage.
 */
export type MODES = ValueOf<typeof MODES>;

//#region Global constants
const ATTRIBUTE_BREAKPOINT = 'breakpoint';
const ATTRIBUTE_MODE = 'mode';
//#endregion Global constants

/**
 * Composant BnumHide.
 * Permet de cacher son contenu selon des points de rupture (breakpoints) définis.
 *
 * @structure Base
 * <bnum-hide breakpoint="small" mode="down">Bonjour</bnum-hide>
 *
 * @attr {'phone' | 'small' | 'touch' | 'normal'} (default:'touch') breakpoint - Le point de rupture à partir duquel cacher l'élément.
 * @attr {'up' | 'down'} (optional) (default:'down') mode - Sens du masquage : 'up' pour cacher au-dessus du breakpoint, 'down' en-dessous.
 */
@Define({ tag: TAG_HIDE })
@Light()
@Observe(ATTRIBUTE_BREAKPOINT, ATTRIBUTE_MODE)
@UpdateAll()
export class HTMLBnumHide extends BnumElementInternal {
	/**
	 * Si le comportement est actif ou non
	 */
	#_disabled = false;
	//#region Private fields
	/**
	 * Liste de requêtes média pour le suivi du breakpoint.
	 */
	#_mediaQueryList: Nullable<MediaQueryList> = null;

	/**
	 * Référence liée de la fonction de gestion du changement de média pour l'abonnement/désabonnement.
	 */
	#_boundHandleChange: (e: MediaQueryListEvent | MediaQueryList) => void;
	//#endregion Private fields

	//#region Lifecycle
	/**
	 * Initialise une nouvelle instance du composant.
	 */
	constructor() {
		super();
		this.#_boundHandleChange = this._handleChange;
	}

	/**
	 * Appelé lorsque le composant est inséré dans le DOM.
	 */
	connectedCallback(): void {
		super.connectedCallback?.();
		this.#_setupListener();
	}

	/**
	 * Appelé lorsque le composant est retiré du DOM.
	 */
	disconnectedCallback(): void {
		this.#_removeListener();
		super.disconnectedCallback?.();
	}

	/**
	 * Met à jour le composant lors d'un changement d'état ou d'attribut.
	 */
	protected _p_update(): void | Nullable<'break'> {
		this.#_setupListener();
	}
	//#endregion Lifecycle

	//#region Publics Methods
	/**
	 * Active la mécanique
	 * @returns Chaîne
	 */
	enable() {
		this.#_disabled = false;
		return this;
	}

	/**
	 * Désactive la mécanique
	 * @returns Chaîne
	 */
	disable() {
		this.#_disabled = true;
		return this;
	}
	//#endregion Publics Methods

	//#region Private methods
	/**
	 * Configure l'écouteur `matchMedia` en fonction des attributs actuels.
	 */
	#_setupListener(): void {
		this.#_removeListener();

		const breakpointKey: keyof typeof BREAKPOINTS = (this.getAttribute(
			ATTRIBUTE_BREAKPOINT,
		) || 'touch') as keyof typeof BREAKPOINTS;
		const mode: MODES = (this.getAttribute(ATTRIBUTE_MODE) ||
			MODES.DOWN) as MODES;

		const width = BREAKPOINTS[breakpointKey];

		if (!width) {
			console.warn(
				`[${TAG_HIDE}] Breakpoint inconnu : ${breakpointKey}. Utilisez: ${Object.keys(BREAKPOINTS).join(', ')}`,
			);
			return;
		}

		const query =
			mode === MODES.UP
				? `(min-width: ${width}px)`
				: `(max-width: ${width - 0.02}px)`;

		this.#_mediaQueryList = window.matchMedia(query);

		this.#_forceHandleChange(this.#_mediaQueryList);

		this.#_mediaQueryList.addEventListener('change', this.#_boundHandleChange);
	}

	/**
	 * Supprime l'écouteur des requêtes média.
	 */
	#_removeListener(): void {
		if (this.#_mediaQueryList) {
			this.#_mediaQueryList.removeEventListener(
				'change',
				this.#_boundHandleChange,
			);
			this.#_mediaQueryList = null;
		}
	}

	/**
	 * Réagit au changement de statut de la requête média.
	 * Si la requête correspond, l'élément est caché.
	 *
	 * @param mq Objet MediaQueryList ou événement associé.
	 */
	@Autobind
	private _handleChange(mq: MediaQueryList | MediaQueryListEvent): void {
		if (this.#_disabled) return;

		const shouldHide = mq.matches;

		if (shouldHide) this.#_hide();
		else this.#_show();
	}

	/**
	 * Réagit au changement de statut de la requête média.
	 * Si la requête correspond, l'élément est caché.
	 *
	 * Même si le comportement est désactivé, il s'effectue quand même.
	 *
	 * @param mq Objet MediaQueryList ou événement associé.
	 */
	#_forceHandleChange(mq: MediaQueryList | MediaQueryListEvent): void {
		const old = this.#_disabled;
		this.#_disabled = false;
		this._handleChange(mq);
		this.#_disabled = old;
	}

	/**
	 * Cache l'élément en ajoutant l'attribut `hidden` et en forçant le style CSS.
	 */
	@SetAttr('hidden', EMPTY_STRING)
	#_hide() {
		this.style.display = 'none';
		this.ariaHidden = 'true';
	}

	/**
	 * Affiche l'élément en retirant l'attribut `hidden` et les styles forcés.
	 */
	#_show() {
		this.removeAttribute('hidden');
		this.style.removeProperty('display');
		this.ariaHidden = 'false';
	}
	//#endregion Private methods
}
